// Identify the dotted SQL identifier at a Monaco cursor and decide whether
// it names a table or a view in the current Object Explorer tree (Phase 1's
// "current SchemaSnapshot" stand-in for the table/view slice).
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 5 (table/view).
//
// Slice 5 sits BEFORE per-Monaco SqlResolverService wiring lands — wiring a
// service per editor instance is a Slice 6+ concern. For table/view-only
// navigation we can do the lookup directly from the source text + the OE
// tree: identifiers in a `FROM x.y` position resolve via dotted parsing,
// and table-vs-view discrimination comes from the OE tree (which is the
// engine-agnostic representation of the current SchemaSnapshot).

import type { ParseOptions, SqlEngine } from '../../sql-grammar/model';
import { findDottedChainStart, makeQualifiedName, parseDottedChain } from '../../sql-resolver/dotted/dottedParts';
import type { DatabaseExplorerNode } from '../../explorer/models/DatabaseTreeModels';
import { useDatabaseStore } from '../../database-objects/store/databaseStore';
import type { DatabaseEngineId } from '@/core/engines';
import type { IdentifierBinding, QualifiedName } from '../types';
import { databaseEngineIdToSqlEngine } from '../../workspace/visual-query-editor/edit/livePullSchemaSnapshot';
import { catalogRelationRef, foldOeComponent, inferOeComponentQuoting } from '../oeIdentifier';
import { defaultSchemaForEngine } from './externalNavigation';
import { extendCursorToIdentEnd } from './cursorIdent';
import {
  catalogComponent,
  catalogDatabaseForTyped,
  isInContextDatabase,
  matchesTypedQualification,
  objectComponentEquals,
  oeDatabaseIdentity,
  preferContextDatabase,
  preferExactTypedComponents,
  typedComponent,
} from './oeTreeQualification';

export type FindBindingDeps = {
  readonly engineId: DatabaseEngineId;
  readonly connectionId: string | null;
  readonly database: string | null;
  /** Allows tests to inject a tree without using the live Zustand store. */
  readonly getTree?: () => readonly DatabaseExplorerNode[];
};

export type TableViewBindingHit = {
  readonly binding: IdentifierBinding;
  /** The CATALOG spelling of the database the TYPED chain bound to
   *  (`Sales.dbo.ARD` matching a `sales` node → `sales`), `undefined` for 1-
   *  and 2-part chains. Distinct from `binding.target.database`, which is
   *  where the OE tree HAPPENED to find the object — for a bare name that can
   *  be any database the tree has loaded, which is why find-references routing
   *  may only trust this field
   *  (`find-references/referenceTargetAtCursor.ts`). */
  readonly explicitDatabase?: string;
};

/** Returns an `IdentifierBinding` for the table or view named at `offset` in
 *  `source`, or `null` when nothing usable is at the cursor. The lookup is
 *  conservative — it returns `null` rather than guessing when the OE tree
 *  doesn't (yet) contain the named object, so the DefinitionProvider stays
 *  silent on cursor positions that don't resolve. */
export function findTableViewBindingAtCursor(
  source: string,
  offset: number,
  deps: FindBindingDeps,
): IdentifierBinding | null {
  return findTableViewHitAtCursor(source, offset, deps)?.binding ?? null;
}

/** Same lookup as {@link findTableViewBindingAtCursor}, plus the database the
 *  cursor's chain spelled out. Callers that ROUTE on the database (find
 *  references / peek, whose identifier index is per (connection, database))
 *  need the typed value; go-to only needs the binding. */
export function findTableViewHitAtCursor(
  source: string,
  offset: number,
  deps: FindBindingDeps,
): TableViewBindingHit | null {
  if (!deps.connectionId) return null;
  // The mock engine has no SQL-grammar projector (it's a behavior simulator,
  // not a parser host). Fall back to mssql parsing rules — the dotted-chain
  // walker only needs identifier-token grammar, which is mssql-like enough
  // for the in-process E2E flows that exercise this path.
  const sqlEngine = databaseEngineIdToSqlEngine(deps.engineId) ?? 'mssql';

  const options: ParseOptions = {
    engine: sqlEngine,
    identifierFolding: defaultFoldingForEngine(sqlEngine),
  };

  // Extend the cursor to the right edge of the identifier under it so the
  // backwards-walking dotted parser sees the WHOLE token (Monaco F12 fires
  // with the cursor mid-word; the SR-RO parser was designed for cursors at
  // the end of a chain).
  const cursorAtEnd = extendCursorToIdentEnd(source, offset);
  const chainStart = findDottedChainStart(source, cursorAtEnd, options);
  const chain = parseDottedChain(source, chainStart, cursorAtEnd, options);
  if (!chain || chain.parts.length === 0) return null;

  // Limit to 1–3 part names — Phase 1 doesn't open `server.db.schema.obj`
  // because the OE tree is per-connection-server already.
  const parts = chain.parts.slice(-3);
  const qualified = makeQualifiedName(parts);

  const treeData = (deps.getTree ?? readLiveTree)();
  const candidate = lookupRelation(treeData, deps, qualified, sqlEngine);
  if (!candidate) return null;
  return {
    binding: {
      kind: candidate.objectType === 'view' ? 'view' : 'table',
      target: candidate.qualified,
    },
    explicitDatabase: catalogDatabaseForTyped(qualified.database, candidate.qualified.database),
  };
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function defaultFoldingForEngine(engine: SqlEngine): ParseOptions['identifierFolding'] {
  // `case-insensitive` for engines whose unquoted identifiers compare folded;
  // `lower` for postgres (folds to lowercase); `upper` for oracle (folds to
  // uppercase). Matches the Resolver's own per-engine defaults closely
  // enough for OE-tree name comparisons.
  if (engine === 'postgres') return 'lower';
  if (engine === 'oracle') return 'upper';
  return 'case-insensitive';
}

type RelationCandidate = {
  readonly objectType: 'table' | 'view';
  readonly qualified: QualifiedName;
  /** The hit satisfied BOTH the requested database and the requested schema
   *  (the engine's default schema when the chain named none) — see
   *  {@link lookupRelation}, which ranks on it ACROSS the two kinds. */
  readonly matchedRequestedSchema: boolean;
};

function lookupRelation(
  treeData: readonly DatabaseExplorerNode[],
  deps: FindBindingDeps,
  qualified: QualifiedName,
  engine: SqlEngine,
): RelationCandidate | null {
  const tableHit = findInTree(treeData, deps, qualified, 'table', engine);
  const viewHit = findInTree(treeData, deps, qualified, 'view', engine);
  const hits = [tableHit, viewHit].filter((h): h is RelationCandidate => h !== null);
  if (hits.length === 0) return null;
  // The two kinds are walked separately, so the ranking each applied
  // internally has to be re-applied across them (`columnAtCursor` /
  // `routineAtCursor` rank the same way, see `oeTreeQualification.ts`):
  //  1. the TAB's own database — with a table `A.dbo.users` and a view
  //     `B.dbo.users` in the tree, F12 from a `B` tab opens B's view;
  //  2. the REQUESTED schema — SQL Server resolves a bare `users` to
  //     `dbo.users`, so a `dbo` VIEW beats a `sales` TABLE of the same name in
  //     the same database;
  //  3. the kind — tables win over views only at an equal rank.
  const inContext = hits.filter((h) =>
    isInContextDatabase(h.qualified.database, deps.engineId, deps.connectionId, deps.database),
  );
  const ranked = inContext.length > 0 ? inContext : hits;
  return ranked.find((h) => h.matchedRequestedSchema) ?? ranked[0];
}

function findInTree(
  treeData: readonly DatabaseExplorerNode[],
  deps: FindBindingDeps,
  qualified: QualifiedName,
  nodeType: 'table' | 'view',
  engine: SqlEngine,
): RelationCandidate | null {
  // The WALK's name filter is deliberately case-INSENSITIVE on every engine:
  // it is a cheap superset, and the precise, engine-aware leaf equality runs
  // once below over everything it collected. A case-sensitive database holds
  // both `dbo.V` and `dbo.v`, so the walk has to SEE both for the choice below
  // to be able to make one.
  const targetName = qualified.foldedName.toLowerCase();
  // The chain's components in BOTH spellings — which half an exact comparison
  // reads is per engine (`oeTreeQualification.ts`, FOLDED vs SPELLED).
  const typedDatabase = typedComponent(qualified.foldedDatabase, qualified.database);
  const typedSchema = typedComponent(qualified.foldedSchema, qualified.schema);
  const typedName = typedComponent(qualified.foldedName, qualified.name);
  // The schema the pick PREFERS below: the one the chain spelled out, else the
  // engine's default — a constant, so it is already its own catalog spelling.
  const requestedSchema = typedSchema ?? catalogComponent(defaultSchemaForEngine(deps.engineId));
  const matches: { db: string | undefined; schema: string | undefined; name: string }[] = [];

  walk(treeData, undefined);
  // Components the chain SPELLED OUT are binding — a `B.dbo.users` cursor may
  // not resolve to `A.dbo.users` (see `oeTreeQualification.ts`). Only what
  // survives this gate can be chosen, fallback included.
  const allowed = matches.filter((m) => {
    if (
      !matchesTypedQualification(
        { database: m.db, schema: m.schema },
        {
          typedDatabase,
          typedSchema,
          contextDatabase: deps.database,
          engineId: deps.engineId,
          connectionId: deps.connectionId,
        },
      )
    ) {
      return false;
    }
    // The LEAF name takes the same engine-aware equality as the schema — it is
    // compared in the same (SQL Server: per-DATABASE) collation, and merging
    // `dbo.V` with `dbo.v` here picked whichever the tree listed first and then
    // routed Find References / Rename at that one.
    return objectComponentEquals(
      deps.engineId,
      deps.connectionId,
      oeDatabaseIdentity(deps.engineId, deps.connectionId, m.db, deps.database),
      typedName,
      m.name,
      'relation',
    );
  });
  if (allowed.length === 0) return null;

  // The tab's database outranks every other one: the default-schema preference
  // below, and the first-match fallback under it, only ever see a foreign
  // database's candidates when the tab's own has none.
  const ranked = preferContextDatabase(
    allowed,
    deps.engineId,
    deps.connectionId,
    deps.database,
    (m) => m.db,
  );
  // Then the REQUESTED schema: the gate above already discarded every candidate
  // whose schema the chain contradicts, so this is a preference among survivors
  // (and for a BARE name the "requested" schema is the engine's default, a
  // constant), not an identity check. It carries NO database clause — the rank
  // above already decided the database, and a clause pinned to the tab's
  // database made this preference dead in the cross-database fallback, where
  // every survivor is foreign: the kind order then decided instead, so a
  // foreign `sales.users` TABLE beat a foreign `dbo.users` VIEW for a bare
  // `users`.
  // The equality is the SAME engine-aware one the gate uses, read under each
  // candidate's own database identity. A blanket `.toLowerCase()` here folded
  // `DBO` into `dbo` after the policy was known, so in a `_CS_` database a bare
  // `users` took whichever of `DBO.users` / `dbo.users` the tree listed first
  // even though only one of them is in the default schema.
  const schemaMatched = ranked.filter((m) =>
    objectComponentEquals(
      deps.engineId,
      deps.connectionId,
      oeDatabaseIdentity(deps.engineId, deps.connectionId, m.db, deps.database),
      requestedSchema,
      m.schema ?? '',
      'schema',
    ),
  );
  const pool = schemaMatched.length > 0 ? schemaMatched : ranked;
  // Last, among equals, the candidate spelled exactly as the user typed it —
  // the probe-free tie-break for a case-insensitive (or not-yet-probed) policy,
  // where `dbo.V` and `dbo.v`, or `DBO.users` and `dbo.users`, both survived.
  const chosen = preferExactTypedComponents(
    pool,
    { schema: qualified.schema, name: qualified.name },
    { schema: (m) => m.schema, name: (m) => m.name },
  )[0];
  // Fold the OE catalog components engine-aware so the identity matches the
  // walker's recorded refs (oracle unquoted → UPPER, pg → lower, quoted names
  // verbatim) — the old blanket `.toLowerCase()` made oracle editor-invoked
  // find-references / rename silently resolve to zero references (CK-B P1-1).
  return {
    objectType: nodeType,
    matchedRequestedSchema: schemaMatched.length > 0,
    qualified: {
      name: chosen.name,
      schema: chosen.schema,
      database: chosen.db,
      // A REFERENCE, not a display join — the same renderer the COLUMN hit and
      // the Object Explorer's own identifier use (`../oeIdentifier.ts`). The
      // per-engine table/view rename emitters rebuild their statement from
      // `name` / `schema` and delimit every component themselves, so nothing
      // emits this `raw` today; it was still the identical landmine the column
      // hit turned out to be, because `dialect.ts`'s `tableText` returns `raw`
      // VERBATIM — a quoted mixed-case relation arrived stripped of the
      // delimiters its identity needs, with a DATABASE component no engine but
      // SQL Server accepts in an `ALTER TABLE` host (and which on Oracle and
      // MySQL IS the owner, so the join repeated it: `JAMSQL.JAMSQL.MixedCase`).
      raw: catalogRelationRef(engine, chosen.schema, chosen.name),
      quoted: inferOeComponentQuoting(engine, chosen.name),
      foldedName: foldOeComponent(engine, chosen.name),
      foldedSchema: chosen.schema ? foldOeComponent(engine, chosen.schema) : undefined,
      foldedDatabase: chosen.db ? foldOeComponent(engine, chosen.db) : undefined,
    },
  };

  function walk(nodes: readonly DatabaseExplorerNode[] | undefined, currentDb: string | undefined): void {
    if (!nodes) return;
    for (const node of nodes) {
      if (node.type === 'connection') {
        const meta = node.metadata as { id?: string } | undefined;
        if (meta?.id !== deps.connectionId) continue;
        walk(node.children as readonly DatabaseExplorerNode[] | undefined, undefined);
        continue;
      }
      if (node.type === 'database') {
        const meta = node.metadata as { name?: string } | undefined;
        walk(node.children as readonly DatabaseExplorerNode[] | undefined, meta?.name);
        continue;
      }
      if (node.type === nodeType) {
        const meta = node.metadata as { objectDetails?: { name?: string; schema?: string } } | undefined;
        const od = meta?.objectDetails;
        if (od?.name && od.name.toLowerCase() === targetName) {
          matches.push({ db: currentDb, schema: od.schema, name: od.name });
        }
        // Tables/views can have child folders (columns, keys); keep walking
        // so we still discover same-named siblings nested under schema groups.
        walk(node.children as readonly DatabaseExplorerNode[] | undefined, currentDb);
        continue;
      }
      walk(node.children as readonly DatabaseExplorerNode[] | undefined, currentDb);
    }
  }
}

function readLiveTree(): readonly DatabaseExplorerNode[] {
  return useDatabaseStore.getState().treeData;
}
